import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { useProducts } from '@/contexts/ProductsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronsUpDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { trSayi, trPara } from '@/lib/sayi';

const reasonLabels = { expired: 'Son Kullanma', damaged: 'Hasarlı', production_waste: 'Üretim Firesi', other: 'Diğer' };
// Sayım farkından doğan fire kayıtları da bu listede; seçilebilir sebep değil
const SEBEP_ADLARI = { ...reasonLabels, count_adjustment: 'Sayım Farkı' };
const KAYNAK_ADLARI = { alis: 'son alış fiyatı', recete: 'reçeteden', son_uretim: 'son üretimden', elle: 'elle girildi' };

export default function WasteRecords() {
  const { products } = useProducts();
  const [records, setRecords] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseProducts, setWarehouseProducts] = useState([]);
  const [productOpen, setProductOpen] = useState(false);
  const [form, setForm] = useState({ warehouse_id: '', product_card_id: '', quantity: '', unit_cost: '', reason: 'other', notes: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/stock/waste').then(r => setRecords(r.data)).catch(() => {});
    api.get('/warehouses').then(r => setWarehouses(r.data)).catch(() => {});
  }, []);

  const handleWarehouseChange = async (warehouseId) => {
    setForm(p => ({ ...p, warehouse_id: warehouseId, product_card_id: '' }));
    setWarehouseProducts([]);
    if (!warehouseId) return;
    try {
      const res = await api.get(`/warehouses/${warehouseId}/stock`);
      const ids = new Set(res.data.map(s => s.product_card_id));
      setWarehouseProducts(products.filter(p => ids.has(p.id)));
    } catch { setWarehouseProducts([]); }
  };

  const selectedProductName = useMemo(() => {
    const p = warehouseProducts.find(x => x.id === form.product_card_id);
    return p ? `${p.code} - ${p.name}` : '';
  }, [form.product_card_id, warehouseProducts]);

  const handleCreate = async () => {
    setLoading(true);
    try {
      await api.post('/stock/waste', {
        ...form,
        quantity: parseFloat(form.quantity),
        unit_cost: parseFloat(form.unit_cost || 0)
      });
      toast.success('Fire kaydi olusturuldu');
      setShowNew(false);
      const res = await api.get('/stock/waste');
      setRecords(res.data);
      setForm({ warehouse_id: '', product_card_id: '', quantity: '', unit_cost: '', reason: 'other', notes: '' });
      setMaliyetKaynagi(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata olustu'); }
    setLoading(false);
  };

  // Maliyet SUNUCUDAN (alış → reçete → son üretim). Yalnızca son alış fiyatı
  // okunuyordu: yarı mamul satın alınmadığı için alan boş, fire tutarı 0
  // yazılıyordu (kullanıcı, 2026-10-02).
  const [maliyetKaynagi, setMaliyetKaynagi] = useState(null);
  const selectProduct = async (pid) => {
    setForm(prev => ({ ...prev, product_card_id: pid, unit_cost: '' }));
    setMaliyetKaynagi(null);
    try {
      const r = await api.get('/stock/waste/birim-maliyet', { params: { product_card_id: pid } });
      const m = r.data?.birim_maliyet || 0;
      setForm(prev => (prev.product_card_id === pid
        ? { ...prev, unit_cost: m > 0 ? String(m) : '' } : prev));
      setMaliyetKaynagi(r.data?.kaynak || 'yok');
    } catch {
      setMaliyetKaynagi('yok');
    }
  };

  return (
    <div data-testid="waste-records-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Fire / Zayi Kayitlari</h1>
          <p className="text-sm text-[#8a7e70] mt-1">{records.length} kayit</p>
        </div>
        <Button onClick={() => setShowNew(true)}
          className="bg-[#7a3535] text-white hover:bg-[#5e2929] font-semibold" data-testid="new-waste-btn">
          <Plus size={16} className="mr-2" /> Yeni Kayit
        </Button>
      </div>

      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tarih</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Urun</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Depo</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Sebep</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Miktar</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Tutar</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.map((r) => (
              <TableRow key={r.id} className="erp-table-row border-[#d0c4b0]">
                <TableCell className="text-sm text-[#3F3E39]">{new Date(r.created_at).toLocaleDateString('tr-TR')}</TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium text-[#1a1916]">{r.product_name}</p>
                    <p className="text-xs text-[#8a7e70] font-mono">{r.product_code}</p>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-[#3F3E39]">{r.warehouse_name}</TableCell>
                <TableCell>
                  <span className="status-badge bg-[#7a3535]/10 text-[#7a3535]">{SEBEP_ADLARI[r.reason] || r.reason}</span>
                </TableCell>
                {/* toFixed ondalığı NOKTA yazar, Türkçede binlik ayraç (ERP.md) */}
                <TableCell className="text-right font-semibold text-[#7a3535]">{trSayi(r.quantity)} {r.unit}</TableCell>
                <TableCell className="text-right font-mono text-sm text-[#7a3535]"
                  title={r.unit_cost ? `${trPara(r.unit_cost)} TL / ${r.unit}${r.maliyet_kaynagi ? ` · ${KAYNAK_ADLARI[r.maliyet_kaynagi] || r.maliyet_kaynagi}` : ''}` : 'Maliyet bulunamadı'}>
                  {trPara(r.total_cost || 0)} TL
                </TableCell>
              </TableRow>
            ))}
            {records.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#8a7e70]">Kayit bulunamadi</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Yeni Fire Kaydi</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Depo *</Label>
              <Select value={form.warehouse_id} onValueChange={handleWarehouseChange}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="waste-warehouse-select">
                  <SelectValue placeholder="Depo secin" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Urun *</Label>
              <Popover open={productOpen} onOpenChange={setProductOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox"
                    disabled={!form.warehouse_id}
                    className="w-full justify-between bg-white border-[#d0c4b0] font-normal text-left"
                    data-testid="waste-product-select">
                    {selectedProductName || (form.warehouse_id ? 'Urun secin...' : 'Once depo secin')}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Urun ara..." />
                    <CommandList>
                      <CommandEmpty>Urun bulunamadi</CommandEmpty>
                      <CommandGroup>
                        {warehouseProducts.map(p => (
                          <CommandItem key={p.id} value={`${p.code} ${p.name}`}
                            onSelect={() => { selectProduct(p.id); setProductOpen(false); }}>
                            <Check className={cn("mr-2 h-4 w-4", form.product_card_id === p.id ? "opacity-100" : "opacity-0")} />
                            {p.code} - {p.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#3F3E39]">Miktar *</Label>
                {/* step="any": olculen deger; sabit adim tarayicida "adim uyusmazligi" yapip formu sessizce engelliyor */}
                <Input type="number" step="any" value={form.quantity}
                  onChange={(e) => setForm(p => ({ ...p, quantity: e.target.value }))}
                  className="bg-white border-[#d0c4b0]" data-testid="waste-quantity-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#3F3E39]">Birim Maliyet</Label>
                <Input type="number" step="any" value={form.unit_cost}
                  onChange={(e) => setForm(p => ({ ...p, unit_cost: e.target.value }))}
                  placeholder={form.product_card_id ? 'otomatik' : ''}
                  className="bg-white border-[#d0c4b0]" data-testid="waste-cost-input" />
                {maliyetKaynagi && maliyetKaynagi !== 'yok' && (
                  <p className="text-[11px] text-[#3d6e4e]" data-testid="waste-cost-kaynak">{KAYNAK_ADLARI[maliyetKaynagi] || maliyetKaynagi}</p>
                )}
                {maliyetKaynagi === 'yok' && (
                  <p className="text-[11px] text-[#8a5c00]" data-testid="waste-cost-yok">
                    Maliyet bulunamadı (alış fiyatı, reçete ya da üretim yok) — elle girin
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Sebep</Label>
              <Select value={form.reason} onValueChange={(v) => setForm(p => ({ ...p, reason: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="waste-reason-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(reasonLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Notlar</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))}
                className="bg-white border-[#d0c4b0]" data-testid="waste-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} className="border-[#d0c4b0]">Iptal</Button>
            <Button onClick={handleCreate} disabled={loading}
              className="bg-[#7a3535] text-white hover:bg-[#5e2929]" data-testid="save-waste-btn">
              {loading ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
